import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { REACT_APP_API_URL } from "../../../../../env";
import { useSelector } from "react-redux";

const LeaveReports = () => {
    const [reportType, setReportType] = useState("Monthly");
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(""); // NEW
    const [date, setDate] = useState("");
    const [employees, setEmployees] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState("");
    const { userType, userId } = useSelector((state) => state.auth);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 20;

    const loadEmployees = async () => {
        try {
            const res = await axios.get(`${REACT_APP_API_URL}api/hrms/report/employees`,{withCredentials:true});
            setEmployees(res.data.success ? res.data.data : []);
        } catch (err) {
            console.error("Employee Fetch Error:", err);
            setEmployees([]);
        }
    };

    useEffect(() => {
        loadEmployees();
    }, []);

    useEffect(() => {
        if (userType === "hrms" && userId) {
            setSelectedEmployee(userId);
        }
    }, [userType, userId]);

    // Fetch Report
    const handleFetchReport = async () => {
        setLoading(true);
        setReportData([]);
        setCurrentPage(1);

        try {
            const params = { reportType };

            if (["Monthly", "Yearly", "Balance"].includes(reportType)) {
                params.year = year;
            }

            if (reportType === "Monthly" && month) {
                params.month = month;
            }

            if (reportType === "Daily") {
                params.date = date;
            }

            if (userType === "hrms") {
                params.empCode = userId;
            } else if (selectedEmployee) {
                params.empCode = selectedEmployee;
            }


            const res = await axios.get(
                `${REACT_APP_API_URL}api/hrms/report/employees-leave-report`,
                {
                    params,
                    withCredentials: true,
                }
            );

            setReportData(res.data.success ? res.data.data : []);
        } catch (err) {
            console.error("Leave Report API Error:", err);
            setReportData([]);
        }

        setLoading(false);
    };

    // Pagination logic
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = reportData.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(reportData.length / recordsPerPage);

    // Export Excel
    const exportExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(reportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "LeaveReport");
        XLSX.writeFile(workbook, "Leave_Report.xlsx");
    };

    // Export PDF
    const exportPDF = () => {
        const doc = new jsPDF("l", "pt", "a4");
        doc.text("Leave Report", 40, 40);

        const columns = Object.keys(reportData[0] || {});
        const rows = reportData.map((item) => Object.values(item));

        doc.autoTable({
            head: [columns],
            body: rows,
            startY: 60,
            styles: { fontSize: 8 },
        });

        doc.save("Leave_Report.pdf");
    };

    const LEAVE_REPORT_COLUMNS = {
        Monthly: [
            { key: "EmployeeID", label: "Employee ID" },
            { key: "EmployeeName", label: "Employee Name" },
            { key: "MonthName", label: "Month" },
            { key: "LeaveType", label: "Leave Type" },
            { key: "NoOfLeaves", label: "No. of Leaves" },
            { key: "DaysTaken", label: "Days Taken" },
        ],

        Daily: [
            { key: "EmployeeID", label: "Employee ID" },
            { key: "EmployeeName", label: "Employee Name" },
            { key: "LeaveType", label: "Leave Type" },
            { key: "StartDate", label: "Start Date" },
            { key: "EndDate", label: "End Date" },
            { key: "Status", label: "Status" },
        ],

        Yearly: [
            { key: "EmployeeID", label: "Employee ID" },
            { key: "EmployeeName", label: "Employee Name" },
            { key: "LeaveType", label: "Leave Type" },
            { key: "NoOfLeaves", label: "Total Leaves" },
            { key: "TotalDaysTaken", label: "Total Days Taken" },
        ],

        Balance: [
            { key: "EmployeeID", label: "Employee ID" },
            { key: "EmployeeName", label: "Employee Name" },
            { key: "LeaveType", label: "Leave Type" },
            { key: "TotalEntitled", label: "Total Entitled" },
            { key: "DaysTaken", label: "Days Taken" },
            { key: "BalanceLeaves", label: "Balance Leaves" },
        ],

    };
    const columns = LEAVE_REPORT_COLUMNS[reportType] || [];

    const getStatusBadgeClass = (status) => {
        const normalized = String(status || "").toLowerCase();
        if (normalized === "approved") return "bg-success-subtle text-success";
        if (normalized === "rejected") return "bg-danger-subtle text-danger";
        if (normalized === "pending") return "bg-warning-subtle text-warning-emphasis";
        return "bg-secondary-subtle text-secondary";
    };

    const renderCellValue = (row, col) => {
        const value = row[col.key];

        if (col.key === "Status") {
            return (
                <span className={`badge rounded-pill px-3 py-2 ${getStatusBadgeClass(value)}`}>
                    {value || "-"}
                </span>
            );
        }

        if (col.key === "LeaveType") {
            return (
                <span className="badge rounded-pill bg-primary-subtle text-primary-emphasis px-3 py-2">
                    {value || "-"}
                </span>
            );
        }

        return value || "-";
    };

    return (
        <div className="container-fluid leave-management-page leave-report-page py-4">
            <div className="leave-page-header d-flex align-items-start justify-content-between flex-wrap gap-3 mb-4">
                <div>
                    <span className="leave-page-kicker">Leave Summary</span>
                    <h3 className="fw-bold mb-1 leave-page-title">Employee Leave Reports</h3>
                    <p className="mb-0 leave-page-description">
                        View comprehensive leave summaries, balances, and approvals.
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="card sticky-top shadow-sm leave-surface-card leave-filter-card leave-report-filter">
                <div className="card-body leave-surface-body">
                    <div className="row g-3 align-items-end">

                        {/* Report Type */}
                        <div className="col-md-4 mb-3">
                            <label className="fw-semibold">Report Type</label>
                            <select
                                className="form-select"
                                value={reportType}
                                onChange={(e) => {
                                    setReportType(e.target.value);
                                    setMonth("");
                                    setDate("");
                                }}
                            >
                                <option value="Monthly">Monthly</option>
                                <option value="Daily">Daily</option>
                                <option value="Yearly">Yearly</option>
                                <option value="Balance">Balance</option>
                            </select>
                        </div>

                        {/* Year */}
                        {(reportType === "Monthly" ||
                            reportType === "Yearly" ||
                            reportType === "Balance") && (
                                <div className="col-md-4 mb-3">
                                    <label className="fw-semibold">Year</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={year}
                                        onChange={(e) => setYear(e.target.value)}
                                    />
                                </div>
                            )}

                        {/* Month (ONLY Monthly) */}
                        {reportType === "Monthly" && (
                            <div className="col-md-4 mb-3">
                                <label className="fw-semibold">Month</label>
                                <select
                                    className="form-select"
                                    value={month}
                                    onChange={(e) => setMonth(e.target.value)}
                                >
                                    <option value="">-- All Months --</option>
                                    {Array.from({ length: 12 }, (_, i) => (
                                        <option key={i + 1} value={i + 1}>
                                            {new Date(0, i).toLocaleString("default", { month: "long" })}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Daily Date */}
                        {reportType === "Daily" && (
                            <div className="col-md-4 mb-3">
                                <label className="fw-semibold">Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                />
                            </div>
                        )}

                        {/* Employee */}
                        {userType !== "hrms" && (
                            <div className="col-md-4 mb-3">
                                <label className="fw-semibold">Employee</label>
                                <select
                                    className="form-select"
                                    value={selectedEmployee}
                                    onChange={(e) => setSelectedEmployee(e.target.value)}
                                >
                                    <option value="">-- Select Employee --</option>
                                    {employees.map((emp) => (
                                        <option key={emp.intUserId} value={emp.intUserId}>
                                            {emp.vchFullName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Show Button */}
                        <div className="col-md-4 col-xl-3 mb-3 d-flex align-items-end">
                            <button className="btn btn-primary w-100 leave-page-action" onClick={handleFetchReport}>
                                Show
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card shadow-sm mt-4 leave-surface-card">
                <div className="card-body leave-surface-body">
                    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
                        <div>
                            <h5 className="fw-bold text-secondary mb-1">Report Data</h5>
                            <p className="mb-0 text-muted small">
                                Showing {reportData.length} record{reportData.length === 1 ? "" : "s"} for the selected criteria.
                            </p>
                        </div>

                        {reportData.length > 0 && (
                            <div className="d-flex flex-wrap gap-2">
                                <button className="btn btn-success" onClick={exportExcel}>
                                    Export Excel
                                </button>
                                <button className="btn btn-danger" onClick={exportPDF}>
                                    Export PDF
                                </button>
                            </div>
                        )}
                    </div>

                    {loading ? (
                        <div className="text-center py-5 leave-loading-state">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="text-primary fw-semibold mt-3 mb-0">Loading report data...</p>
                        </div>
                    ) : (
                        <div className="leave-table-shell leave-report-table-shell">
                            <table className="table table-hover align-middle mb-0 leave-table">
                                <thead className="table-light">
                                    <tr>
                                        {columns.map((col) => (
                                            <th key={col.key} scope="col">{col.label}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentRecords.length === 0 ? (
                                        <tr>
                                            <td colSpan="20" className="text-center text-danger fw-bold">
                                                No Records Found
                                            </td>
                                        </tr>
                                    ) : (
                                        currentRecords.map((row, i) => (
                                            <tr key={i}>
                                                {columns.map((col) => (
                                                    <td key={col.key}>{renderCellValue(row, col)}</td>
                                                ))}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {reportData.length > 0 && (
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mt-3">
                            <button
                                className="btn btn-outline-primary"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(currentPage - 1)}
                            >
                                Previous
                            </button>
                            <span className="fw-bold">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                className="btn btn-outline-primary"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(currentPage + 1)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LeaveReports;
